import {
  Alert,
  Collapse,
  SvgIcon,
  SxProps,
  styled,
  AlertColor,
  useTheme,
  Typography
} from "@mui/material";
import { mergeSx } from "merge-sx";
import { ReactNode } from "react";

import {
  CheckmarkCircle24Px,
  Error24Px,
  Info24Px,
  Warning24Px
} from "../icons/autostoreIcons/icons24px";

type AlertBannerProps = {
  severity?: AlertColor;
  children?: ReactNode;
  sx?: SxProps;
  collapseSx?: SxProps;
  skipCustomTypography?: boolean;
  icon?: ReactNode;
};

const StyledAlert = styled(Alert, {
  shouldForwardProp: (propName: string) =>
    !["backgroundColor", "borderColor"].includes(propName)
})<{ backgroundColor: string; borderColor: string }>(
  ({ backgroundColor, borderColor }) => ({
    backgroundColor,
    border: `1px solid ${borderColor}`,
    paddingLeft: "48px",
    paddingRight: "48px",
    display: "flex",
    alignItems: "center",
    gap: "12px"
  })
);

const StyledTypography = styled(Typography)`
  color: black;
  font-size: 1.25rem;
  line-height: 24px;
`;

const severityToIconMap = {
  error: <SvgIcon component={Error24Px} width={6} height={6} />,
  warning: <SvgIcon component={Warning24Px} width={6} height={6} />,
  info: <SvgIcon component={Info24Px} width={6} height={6} />,
  success: <SvgIcon component={CheckmarkCircle24Px} width={6} height={6} />
} as const;

/**
 * A banner meant for displaying a message which can be used as a full-width
 * banner or a smaller inline banner.
 *
 * Controlling the severity prop will change the color of the banner and the
 * icon shown inside of it. Defaults to an error banner.
 */
export const AlertBanner = ({
  severity = "error",
  children,
  sx,
  collapseSx,
  skipCustomTypography = false,
  icon
}: AlertBannerProps) => {
  const { palette } = useTheme();

  const styles = {
    ".MuiAlert-icon > svg": {
      fontSize: "30px"
    }
  };

  return (
    <Collapse in={!!children} sx={mergeSx({ overflow: "hidden" }, collapseSx)}>
      <StyledAlert
        severity={severity}
        backgroundColor={palette[severity].light}
        borderColor={palette[severity].main}
        icon={icon === undefined ? severityToIconMap[severity] : icon}
        sx={mergeSx(styles, sx)}
      >
        {skipCustomTypography ? (
          children
        ) : (
          <StyledTypography>{children}</StyledTypography>
        )}
      </StyledAlert>
    </Collapse>
  );
};
